import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Buildings, BuildingApartment, Users, ArrowRight, Sparkle, CheckCircle,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

function slugify(v) {
  return v.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
}

const MODES = {
  organization: {
    endpoint: "organization",           // maps to /api/signup/company (kept name in-body only)
    apiPath: "company",
    label: "An Organization",
    subtitle: "Run your own HR — single company",
    icon: Buildings,
    color: "adb-navy",
    orgLabel: "Organization name",
    orgPlaceholder: "Acme Technologies Pvt Ltd",
    bodyKey: "company_name",
    ctaText: "Create Organization",
    highlight: "border-adb-navy bg-adb-navy/[0.03] ring-1 ring-adb-navy/20",
    iconClass: "text-adb-navy",
    submitBtn: "bg-adb-navy hover:bg-adb-navy-800",
  },
  group: {
    endpoint: "group",
    apiPath: "group",
    label: "A Group of Companies",
    subtitle: "One parent, many sister companies",
    icon: BuildingApartment,
    color: "zenith-teal",
    orgLabel: "Group name",
    orgPlaceholder: "Meridian Holdings Group",
    bodyKey: "group_name",
    ctaText: "Create Group",
    highlight: "border-zenith-teal bg-zenith-teal/[0.06] ring-1 ring-zenith-teal/25",
    iconClass: "text-zenith-teal",
    submitBtn: "bg-gradient-to-r from-zenith-teal to-adb-navy hover:brightness-110",
  },
  agency: {
    endpoint: "agency",
    apiPath: "agency",
    label: "An HR Agency",
    subtitle: "Serve multiple client companies",
    icon: Users,
    color: "zenith-magenta",
    orgLabel: "Agency name",
    orgPlaceholder: "Acme HR Consulting",
    bodyKey: "agency_name",
    ctaText: "Create HR Agency",
    highlight: "border-zenith-magenta bg-zenith-magenta/[0.06] ring-1 ring-zenith-magenta/25",
    iconClass: "text-zenith-magenta",
    submitBtn: "bg-gradient-to-r from-zenith-magenta to-adb-navy hover:brightness-110",
  },
};

export default function Signup() {
  const nav = useNavigate();
  const { refreshMe } = useAuth();
  const [mode, setMode] = useState("organization");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    orgName: "", slug: "", adminName: "", adminEmail: "", password: "",
  });

  const cfg = MODES[mode];
  const setOrg = (v) => setForm((f) => ({ ...f, orgName: v, slug: f.slug || slugify(v) }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    const url = `${API}/api/signup/${cfg.apiPath}`;
    const payload = {
      [cfg.bodyKey]: form.orgName,
      slug: form.slug,
      admin_name: form.adminName,
      admin_email: form.adminEmail,
      password: form.password,
    };
    try {
      await axios.post(url, payload, { withCredentials: true });
      await refreshMe();
      toast.success(`Welcome to Zenith360! Your ${cfg.label.replace(/^A[n]? /, "").toLowerCase()} is live — 14-day free trial started.`);
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] bg-[#f7f6f2]" data-testid="signup-page">
      {/* Left hero */}
      <div className="hidden lg:flex relative flex-col justify-between bg-gradient-to-br from-adb-navy via-zenith-blue to-adb-navy-800 text-white p-12 overflow-hidden">
        <div className="absolute -top-32 -left-24 w-96 h-96 rounded-full bg-zenith-teal/25 blur-3xl" />
        <div className="absolute -bottom-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-zenith-magenta/25 blur-3xl" />
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-3 group" data-testid="signup-brand">
            <div className="w-16 h-16 bg-white rounded-md p-1.5 border border-white/20 shadow-md grid place-items-center">
              <img src="/zenith360-mark.png" alt="Zenith360" className="max-w-full max-h-full object-contain" />
            </div>
            <div>
              <div className="font-display text-2xl font-bold tracking-tight leading-none">Zenith<span className="text-zenith-green">360</span> HR</div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-zenith-green/80 mt-1">Your complete HR Ecosystem</div>
            </div>
          </Link>
          <div className="mt-16 max-w-lg">
            <div className="text-[11px] uppercase tracking-[0.3em] text-zenith-green font-bold">Start free · 14-day trial</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 leading-[1.05]">
              Three ways in.<br />
              <span className="font-serif-display italic bg-gradient-to-r from-zenith-teal via-zenith-green to-white bg-clip-text text-transparent">One product.</span>
            </h1>
            <ul className="mt-8 space-y-3 text-white/85 text-sm">
              {[
                "Full Enterprise features unlocked for 14 days",
                "Add users free — admin logins are always free",
                "Minimum 3 users when you subscribe (₹69 / seat / month)",
                "Cross-tenant reporting for Groups & Agencies",
                "Cancel or switch pack anytime, no lock-in",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <CheckCircle size={18} weight="fill" className="text-zenith-green mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="relative text-[11px] text-white/50">
          Already have an account? <Link to="/login" className="underline decoration-zenith-green/60 hover:text-white">Sign in →</Link>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-2.5">
            <div className="w-11 h-11 bg-white rounded-sm p-1 border border-zinc-200 grid place-items-center">
              <img src="/zenith360-mark.png" alt="Zenith360" className="max-w-full max-h-full object-contain" />
            </div>
            <div className="font-display text-lg font-bold">Zenith<span className="text-zenith-green">360</span> HR</div>
          </div>

          <div className="mb-6">
            <div className="text-xs uppercase tracking-[0.28em] text-adb-navy font-bold">Create your account</div>
            <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Get started free.</h2>
            <p className="text-sm text-zinc-500 mt-2">Pick who you are. No credit card required for the 14-day trial.</p>
          </div>

          {/* Mode picker — 3 tiles */}
          <div className="grid grid-cols-3 gap-2 mb-5" data-testid="signup-mode-picker">
            {Object.entries(MODES).map(([key, m]) => {
              const active = mode === key;
              const Icon = m.icon;
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`signup-mode-${key}`}
                  onClick={() => setMode(key)}
                  className={`text-left p-3 border rounded-md transition-all ${
                    active ? m.highlight : "border-zinc-200 hover:border-zinc-300"
                  }`}
                >
                  <Icon size={18} className={active ? m.iconClass : "text-zinc-400"} weight={active ? "fill" : "regular"} />
                  <div className="font-display font-bold text-[13px] mt-2 leading-tight">{m.label}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5 leading-tight">{m.subtitle}</div>
                </button>
              );
            })}
          </div>

          <form onSubmit={submit} className="space-y-3.5" data-testid="signup-form">
            <div>
              <Label className="text-xs">{cfg.orgLabel}</Label>
              <Input
                data-testid="signup-org-name"
                value={form.orgName}
                onChange={(e) => setOrg(e.target.value)}
                placeholder={cfg.orgPlaceholder}
                required minLength={2}
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Workspace slug</Label>
              <div className="mt-1 flex items-center gap-2">
                <div className="text-xs text-zinc-400 font-mono-plex">zenith360.com/</div>
                <Input
                  data-testid="signup-slug"
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value) }))}
                  placeholder="acme"
                  required minLength={2} pattern="[a-z0-9\-]+"
                  className="rounded-sm font-mono-plex"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Your name</Label>
                <Input
                  data-testid="signup-admin-name"
                  value={form.adminName}
                  onChange={(e) => setForm((f) => ({ ...f, adminName: e.target.value }))}
                  placeholder="Priya Nair" required minLength={2}
                  className="mt-1 rounded-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Work email</Label>
                <Input
                  data-testid="signup-admin-email"
                  type="email" required
                  value={form.adminEmail}
                  onChange={(e) => setForm((f) => ({ ...f, adminEmail: e.target.value }))}
                  placeholder="you@company.com"
                  className="mt-1 rounded-sm"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Password (8+ chars)</Label>
              <Input
                data-testid="signup-password"
                type="password" required minLength={8}
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="At least 8 characters"
                className="mt-1 rounded-sm"
              />
            </div>

            <Button
              type="submit"
              data-testid="signup-submit"
              disabled={busy}
              className={`w-full h-11 rounded-sm font-semibold text-white transition-all ${cfg.submitBtn}`}
            >
              {busy
                ? "Creating your workspace…"
                : (<span className="inline-flex items-center gap-2">{cfg.ctaText} <ArrowRight size={16} /></span>)}
            </Button>

            <div className="text-[11px] text-zinc-500 flex items-center gap-1.5 justify-center pt-2">
              <Sparkle size={11} className="text-zenith-green" weight="fill" />
              Enterprise features unlocked for 14 days. No card needed.
            </div>
          </form>

          <div className="mt-8 text-sm text-zinc-500 text-center">
            Already have an account? <Link to="/login" className="text-adb-navy font-semibold hover:underline" data-testid="signup-to-login">Sign in →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
